import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { App, type AppState } from '@capacitor/app';
import { useQueryClient } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useHealthKit } from '@/hooks/useHealthKit';
import { ensureSession } from '@/lib/ensureSession';
import { getHealthReader } from '@/lib/health/reader';
import type {
  DailyMetricResult,
  DateRange,
  HealthScope,
} from '@/lib/health/HealthDataReader';
import { CORE_HEALTH_SCOPES } from '@/lib/health/HealthDataReader';
import {
  trackHealthPermissionDenied,
  trackHealthPermissionGranted,
  trackHealthSyncCompleted,
  trackHealthSyncFailed,
  type SyncMetricsPresent,
} from '@/lib/health/analytics';
import { recomputeDailyScores } from '@/lib/health/recomputeScores';
import { triggerScoreRecompute } from '@/lib/health/triggerScoreRecompute';
import { useHealthSyncStatus } from '@/stores/healthSyncStatus';

const SYNC_THROTTLE_MS = 5 * 60 * 1000; // 5 minutes
const LAST_SYNCED_KEY = 'healthkit_last_synced_at';
const BACKFILL_DAYS = 30;
const FOREGROUND_INCREMENTAL_DAYS = 3;

/** Maps @capgo/capacitor-health WorkoutType values to our `workouts.type` strings. */
const HK_WORKOUT_TYPE_MAP: Record<string, string> = {
  running: 'running',
  runningTreadmill: 'running',
  walking: 'walking',
  hiking: 'walking',
  cycling: 'cycling',
  bikingStationary: 'cycling',
  swimming: 'swimming',
  swimmingPool: 'swimming',
  swimmingOpenWater: 'swimming',
  rowing: 'rowing',
  rowingMachine: 'rowing',
  traditionalStrengthTraining: 'strength',
  functionalStrengthTraining: 'strength',
  strengthTraining: 'strength',
  weightlifting: 'strength',
  crossTraining: 'strength',
  highIntensityIntervalTraining: 'hiit',
  bootCamp: 'hiit',
  mixedCardio: 'cardio',
  elliptical: 'cardio',
  stairClimbing: 'cardio',
  stairClimbingMachine: 'cardio',
  stepTraining: 'cardio',
  cardioDance: 'cardio',
  jumpRope: 'cardio',
  yoga: 'yoga',
  pilates: 'yoga',
  mindAndBody: 'yoga',
  flexibility: 'yoga',
  stretching: 'yoga',
  other: 'other',
};

const HK_WORKOUT_DISPLAY: Record<string, string> = {
  running: 'Run',
  runningTreadmill: 'Treadmill Run',
  walking: 'Walk',
  hiking: 'Hike',
  cycling: 'Cycle',
  bikingStationary: 'Indoor Cycle',
  swimming: 'Swim',
  rowing: 'Row',
  rowingMachine: 'Row',
  traditionalStrengthTraining: 'Strength',
  functionalStrengthTraining: 'Strength',
  strengthTraining: 'Strength',
  weightlifting: 'Weightlifting',
  crossTraining: 'Cross Training',
  highIntensityIntervalTraining: 'HIIT',
  bootCamp: 'Bootcamp',
  mixedCardio: 'Cardio',
  elliptical: 'Elliptical',
  stairClimbing: 'Stair Climber',
  yoga: 'Yoga',
  pilates: 'Pilates',
  other: 'Workout',
};

const mapWorkoutType = (hk: string): string => HK_WORKOUT_TYPE_MAP[hk] ?? 'other';
const displayWorkoutName = (hk: string): string =>
  HK_WORKOUT_DISPLAY[hk] ?? hk.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());

const toLocalDateKey = (iso: string): string => {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const readLastSynced = (): number | null => {
  try {
    const raw = localStorage.getItem(LAST_SYNCED_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
};

const writeLastSynced = (ts: number): void => {
  try {
    localStorage.setItem(LAST_SYNCED_KEY, String(ts));
  } catch {
    // ignore
  }
};

export const getHealthKitLastSyncedAt = (): number | null => readLastSynced();

interface SyncResult {
  daysWritten: number;
  workouts: number;
  metrics_present: SyncMetricsPresent;
  errors: string[];
}

interface DayRow {
  user_id: string;
  date: string;
  steps: number | null;
  active_energy_kcal: number | null;
  sleep_minutes: number | null;
  resting_hr: number | null;
  hrv_sdnn: number | null;
  vo2max: number | null;
  source: string;
  last_synced_at: string;
  updated_at: string;
}

/**
 * Resilient per-metric fetch — a failure in one metric never aborts the
 * whole backfill. Returns the metric's daily values on success, or an
 * empty array on failure with the error captured in `errors`.
 */
const safeFetch = async <T,>(
  label: string,
  fn: () => Promise<T[]>,
  errors: string[],
): Promise<T[]> => {
  try {
    return await fn();
  } catch (e) {
    errors.push(`${label}: ${e instanceof Error ? e.message : 'unknown'}`);
    return [];
  }
};

/**
 * Foreground-only Health sync — Phase 2a.
 *
 * - Reads through the platform-agnostic `HealthDataReader` seam (iOS today,
 *   Android/Health Connect swap-in later).
 * - Merges steps + active energy + sleep + restingHR + HRV into ONE row per
 *   `(user_id, date)` and upserts once — the steps path and new-metrics path
 *   share the same write.
 * - Per-metric failures are captured but do not abort the backfill. Whatever
 *   succeeded is written; the failure reason is recorded on `health_sync_state`
 *   and fired as `health_sync_failed`; `connected` stays true so the next
 *   foreground sync retries the gap.
 * - Missing days are written as `null`, never `0`.
 * - Active energy is still pulled via `useHealthKit` (kept for continuity
 *   with the existing pipe — moving it behind the reader is deferred; see
 *   `TODO(reader)` markers).
 */
export const useHealthSync = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const reader = useMemo(() => getHealthReader(), []);
  const isAvailable = reader.isAvailable();

  // Workouts still come through the legacy hook — workout writes are not
  // yet part of the reader interface. Metrics (steps, active energy, sleep,
  // resting HR, HRV) all flow through the reader and share a single upsert.
  const { queryWorkoutsRange } = useHealthKit();

  const [syncing, setSyncing] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(() => readLastSynced());
  const inFlightRef = useRef<Promise<SyncResult | null> | null>(null);

  const runSync = useCallback(
    async (opts: { force?: boolean; days?: number } = {}): Promise<SyncResult | null> => {
      if (!isAvailable || !user) return null;
      const previous = readLastSynced();
      if (!opts.force && previous && Date.now() - previous < SYNC_THROTTLE_MS) {
        return null;
      }
      if (inFlightRef.current) return inFlightRef.current;

      const work = (async (): Promise<SyncResult | null> => {
        setSyncing(true);
        useHealthSyncStatus.getState().setStatus('syncing');
        try {
          const days = opts.days ?? BACKFILL_DAYS;
          const end = new Date();
          const start = new Date();
          start.setDate(end.getDate() - days);
          start.setHours(0, 0, 0, 0);
          const range: DateRange = { start, end };

          const errors: string[] = [];

          // Fetch all metrics in parallel; each is individually resilient.
          const emptyResult: DailyMetricResult = { metrics: [], sampleCount: 0 };
          const safeRead = async (
            label: string,
            fn: () => Promise<DailyMetricResult>,
          ): Promise<DailyMetricResult> => {
            try { return await fn(); }
            catch (e) {
              errors.push(`${label}: ${e instanceof Error ? e.message : 'unknown'}`);
              return emptyResult;
            }
          };

          const [
            stepsRes,
            energyRes,
            sleepRes,
            hrRes,
            hrvRes,
            vo2Res,
            workouts,
          ] = await Promise.all([
            safeRead('steps', () => reader.getSteps(range)),
            safeRead('active_energy', () => reader.getActiveEnergy(range)),
            safeRead('sleep', () => reader.getSleep(range)),
            safeRead('resting_hr', () => reader.getRestingHR(range)),
            safeRead('hrv', () => reader.getHRV(range)),
            safeRead('vo2_max', () => reader.getVO2Max(range)),
            safeFetch('workouts', () => queryWorkoutsRange(start, end), errors),
          ]);

          const stepsByDay = stepsRes.metrics;
          const energyByDay = energyRes.metrics;
          const sleepByDay = sleepRes.metrics;
          const hrByDay = hrRes.metrics;
          const hrvByDay = hrvRes.metrics;

          // Merge everything by (user_id, date) into ONE row per day.
          const rowsByDate = new Map<string, DayRow>();
          const nowIso = new Date().toISOString();
          const getOrCreate = (date: string): DayRow => {
            let row = rowsByDate.get(date);
            if (!row) {
              row = {
                user_id: user.id,
                date,
                steps: null,
                active_energy_kcal: null,
                sleep_minutes: null,
                resting_hr: null,
                hrv_sdnn: null,
                vo2max: null,
                source: 'healthkit',
                last_synced_at: nowIso,
                updated_at: nowIso,
              };
              rowsByDate.set(date, row);
            }
            return row;
          };

          const present: SyncMetricsPresent = {
            sleep: 0,
            restingHR: 0,
            hrv: 0,
            steps: 0,
            activeEnergy: 0,
          };

          // Days in the query window (post-fillMissingDays).
          const daysInRange = {
            sleep: sleepByDay.length,
            resting_hr: hrByDay.length,
            hrv: hrvByDay.length,
            steps: stepsByDay.length,
            active_energy: energyByDay.length,
          };
          // Raw samples returned by the native plugin (pre-aggregation).
          // This is the only signal that tells us whether HealthKit
          // actually handed anything back.
          const sampleCount = {
            sleep: sleepRes.sampleCount,
            resting_hr: hrRes.sampleCount,
            hrv: hrvRes.sampleCount,
            steps: stepsRes.sampleCount,
            active_energy: energyRes.sampleCount,
          };

          for (const m of stepsByDay) {
            if (m.value == null || m.value <= 0) continue;
            getOrCreate(m.date).steps = m.value;
            present.steps += 1;
          }
          for (const m of energyByDay) {
            if (m.value == null || m.value <= 0) continue;
            getOrCreate(m.date).active_energy_kcal = m.value;
            present.activeEnergy += 1;
          }
          for (const m of sleepByDay) {
            if (m.value == null || m.value <= 0) continue;
            getOrCreate(m.date).sleep_minutes = m.value;
            present.sleep += 1;
          }
          for (const m of hrByDay) {
            if (m.value == null || m.value <= 0) continue;
            getOrCreate(m.date).resting_hr = m.value;
            present.restingHR += 1;
          }
          for (const m of hrvByDay) {
            if (m.value == null || m.value <= 0) continue;
            getOrCreate(m.date).hrv_sdnn = m.value;
            present.hrv += 1;
          }
          // VO₂ max — sparse, take latest non-null.
          for (const m of vo2Res.metrics) {
            if (m.value == null || m.value <= 0) continue;
            getOrCreate(m.date).vo2max = Math.round(m.value * 10) / 10;
          }

          // eslint-disable-next-line no-console

          const rows = Array.from(rowsByDate.values()).filter(
            (r) =>
              r.steps != null ||
              r.active_energy_kcal != null ||
              r.sleep_minutes != null ||
              r.resting_hr != null ||
              r.hrv_sdnn != null ||
              r.vo2max != null,
          );

          await ensureSession();

          let daysWritten = 0;
          if (rows.length > 0) {
            const { error: metricsErr } = await supabase
              .from('health_daily_metrics')
              .upsert(rows, { onConflict: 'user_id,date' });
            if (metricsErr) {
              errors.push(`upsert_metrics: ${metricsErr.message}`);
            } else {
              daysWritten = rows.length;
            }
          }

          let workoutsCount = 0;
          if (workouts.length > 0) {
            const workoutRows = workouts.map((w) => {
              const startIso = w.startDate;
              const endIso = w.endDate;
              const durationSec = w.duration || 0;
              const externalId = `${startIso}|${w.workoutType}|${Math.round(durationSec)}`;
              return {
                user_id: user.id,
                name: displayWorkoutName(w.workoutType),
                type: mapWorkoutType(w.workoutType),
                date: toLocalDateKey(startIso),
                duration: Math.max(0, Math.round(durationSec / 60)),
                source: 'healthkit',
                external_id: externalId,
                notes: `Synced from Apple Health · ${w.sourceName ?? 'Apple Watch'}`,
              };
            });
            const { error: workoutErr } = await supabase
              .from('workouts')
              .upsert(workoutRows, {
                onConflict: 'user_id,source,external_id',
                ignoreDuplicates: false,
              });
            if (workoutErr) {
              errors.push(`upsert_workouts: ${workoutErr.message}`);
            } else {
              workoutsCount = workoutRows.length;
            }
          }

          const finishedAt = Date.now();
          writeLastSynced(finishedAt);
          setLastSyncedAt(finishedAt);

          // Persist sync state. `connected` stays true regardless of
          // per-metric errors — the next foreground pass retries the gaps.
          // Always writes even when 0 rows were produced — that itself is
          // signal ("connected but nothing to sync"), and it's needed for
          // the three-way gate on Profile.
          // Upsert (not update): users who never opened the connect sheet have
          // no health_sync_state row, so an update would silently match zero
          // rows and discard all telemetry. granted_scopes / probe_results /
          // auth_status stay untouched — those are owned by connect().
          try {
            await supabase
              .from('health_sync_state')
              .upsert(
                {
                  user_id: user.id,
                  platform: reader.platform,
                  connected: true,
                  last_synced_at: new Date(finishedAt).toISOString(),
                  last_error: errors.length > 0 ? errors.join(' | ').slice(0, 500) : null,
                  debug_info: {
                    backfill_days: days,
                    days_in_range: daysInRange,
                    sample_count: sampleCount,
                    days_with_data: { ...present },
                    errors,
                    at: new Date(finishedAt).toISOString(),
                  } as never,
                },
                { onConflict: 'user_id' },
              );
          } catch {
            // Non-fatal.
          }

          if (daysWritten > 0) {
            queryClient.invalidateQueries({ queryKey: ['health-daily-metrics', user.id] });
            // Fresh steps → server-side health-score recompute (debounced).
            triggerScoreRecompute(user.id, queryClient);
          }
          queryClient.invalidateQueries({ queryKey: ['health-sync-state', user.id] });
          if (workoutsCount > 0) {
            queryClient.invalidateQueries({ queryKey: ['workouts'] });
          }

          if (errors.length > 0) {
            trackHealthSyncFailed(errors.slice(0, 3).join(' | '));
          } else {
            trackHealthSyncCompleted(daysWritten, present);
          }


          // Post-sync: recompute today's Recovery + Strain scores.
          // Fire-and-forget — scoring failures must not fail the sync.
          void recomputeDailyScores(user.id).then((res) => {
            if (res) {
              queryClient.invalidateQueries({
                queryKey: ['health-daily-score', user.id],
              });
            }
          });

          if (errors.length > 0) {
            useHealthSyncStatus.getState().setStatus('error');
          } else {
            useHealthSyncStatus.getState().setDone(finishedAt);
          }

          return { daysWritten, workouts: workoutsCount, metrics_present: present, errors };
        } catch (e) {
          const reason = e instanceof Error ? e.message : 'unknown';
          trackHealthSyncFailed(reason);
          useHealthSyncStatus.getState().setStatus('error');
          return null;
        } finally {
          setSyncing(false);
          inFlightRef.current = null;
        }
      })();

      inFlightRef.current = work;
      return work;
    },
    [isAvailable, user, reader, queryWorkoutsRange, queryClient],
  );

  /**
   * Request read permissions for the four core scopes and, on any grant,
   * kick off the initial backfill. Records the granted scopes on
   * `health_sync_state`. Returns the actually-granted scopes so callers
   * can reflect partial-grant state honestly.
   */
  const connect = useCallback(
    async (scopes: HealthScope[] = CORE_HEALTH_SCOPES): Promise<HealthScope[]> => {
      if (!isAvailable || !user) return [];
      // Always request vo2Max alongside the caller's scopes. It's optional
      // and sparse — a denial must NOT block the core grant, so callers
      // continue to treat `granted` against CORE_HEALTH_SCOPES.
      const requested: HealthScope[] = scopes.includes('vo2Max')
        ? scopes
        : [...scopes, 'vo2Max'];
      const { granted: grantedAll } = await reader.requestPermissions(requested);
      // Return only scopes the caller asked for — vo2Max is bonus, must not
      // affect callers that compare against a core-scope set.
      const granted = grantedAll.filter((s) => scopes.includes(s));

      if (granted.length > 0) {
        trackHealthPermissionGranted(granted);
        await runSync({ days: BACKFILL_DAYS });
        queryClient.invalidateQueries({ queryKey: ['health-daily-score', user.id] });
        queryClient.invalidateQueries({ queryKey: ['health-daily-metrics', user.id] });
      } else {
        trackHealthPermissionDenied();
      }
      queryClient.invalidateQueries({ queryKey: ['health-sync-state', user.id] });
      return granted;
    },
    [isAvailable, user, reader, runSync, queryClient],
  );


  // Foreground sync — mount + resume. Incremental (last 3 days) covers late
  // HealthKit writes; the throttle guarantees no more than 1 pass per 5 min.
  useEffect(() => {
    if (!isAvailable || !user) return;
    void runSync({ days: FOREGROUND_INCREMENTAL_DAYS });

    let handle: { remove: () => void } | undefined;
    let cancelled = false;
    void App.addListener('appStateChange', (state: AppState) => {
      if (state.isActive) void runSync({ days: FOREGROUND_INCREMENTAL_DAYS });
    })
      .then((h) => {
        if (cancelled) {
          void h.remove();
        } else {
          handle = h;
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      handle?.remove();
    };
  }, [isAvailable, user, runSync]);

  return { syncing, lastSyncedAt, runSync, connect, isAvailable, platform: reader.platform };
};
